<template>
  <!--
    SLOTS
      - NORMAL - this is the content of the modal
      - actions - this is where you will put some actions for the modal
  -->
  <div class="modal" v-if="isVisible" :class="{'modal__is-relative': relativeToParent}">
    <div class="modal__bg" v-if="hasBg"></div>
      <div class="modal__container" ref="container" :style="modalWidth">
        <div class="modal__header-actions">
          <slot name="header-buttons"></slot>
          <div class="modal__close-btn" @click="close" v-if="closeButton">
            <svg-icon :icon="cancel" :width="12" :height="12"></svg-icon>
          </div>
        </div>
        <div class="modal__body">
          <div class="modal__title">
            {{title}}
          </div>
          <template v-if="!loading">
            <div class="modal__content"
              :class="{'modal__content--no-padding': removePadding}"
              ref="content"
              :style="contentHeightStyle">

              <slot></slot>
            </div>
            <div v-if="!hideActions" class="modal__actions">
              <slot name="actions"></slot>
            </div>
          </template>
          <div v-else class="modal__loading-container">
            <loading-dots/>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
  import SvgIcon from '@/components/common/svgIcon'
  import LoadingDots from '@/components/common/loaderDots'
  import cancel from '@/assets/svg/cancel.svg'

  const HEIGHT_OF_ACTIONS = 52
  const PADDING_OF_MODAL = 44 // equal to 2 * border of __container + 2 * padding of __content

  export default {
    components: {
      SvgIcon,
      LoadingDots
    },
    props: {
      title: {
        type: String,
        default: ''
      },
      relativeToParent: {
        type: Boolean,
        default: false
      },
      hasBg: {
        type: Boolean,
        default: true
      },
      initiallyDisplay: {
        type: Boolean,
        default: false
      },
      removePadding: {
        type: Boolean,
        default: false
      },
      closeButton: {
        type: Boolean,
        default: false
      },
      autoWidth: {
        type: Boolean,
        default: false
      },
      autoHeight: {
        type: Boolean,
        default: false
      },
      width: {
        type: Number,
        default: null
      },
      heightOverride: {
        type: Number,
        default: null
      },
      // min and max
      heightRange: {
        type: Object,
        default: null
      },
      loading: {
        type: Boolean,
        default: false
      },
      onClose: {
        type: Function,
        default: () => {}
      },
      hideActions: {
        type: Boolean,
        default: false
      },
      enableBackgroundClickToClose: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        isVisible: this.initiallyDisplay,
        cancel,
        heightOfContent: null,
        setup: false
      }
    },
    computed: {
      contentHeightStyle () {
        let style = {}
        if (this.heightOfContent) {
          style = { ...style, height: this.heightOfContent + 'px' }
        }
        if (this.heightRange) {
          style = {
            ...style,
            'min-height': this.heightRange.min + 'px',
            'max-height': this.heightRange.max + 'px'
          }
        }
        return style
      },
      modalWidth () {
        const width = this.width ? String(this.width) + 'px' : '90%'
        return this.autoWidth ? {maxWidth: 'none', width} : {width}
      }
    },
    methods: {
      /**
       * THis will extend a scrolling to the selected elements,  this is used when you want to
       * view the entire array of seletable items in the modal, when somtimes it can be hard to see
       * of even scroll to the end of the modal to view these items on the modal itself
       */
      extendScroll (cssSelector) {
        setTimeout(() => {
          if (this.$el.getElementsByClassName) {
            let content = this.$el.getElementsByClassName('modal__content')[0]
            let elements = content.getElementsByClassName(cssSelector)
            for (let element of elements) {
              element.addEventListener('click', function () {
                setTimeout(() => {
                  content.scrollBy(0, 200)
                }, 30)
              })
            }
          }
        }, 30)
      },
      open () {
        this.isVisible = true
        this.setup = true
        window.addEventListener('click', this.handleDocumentClick, true)
      },
      close () {
        if (!this.isVisible) return
        this.isVisible = false
        this.breakdown()
        window.removeEventListener('click', this.handleDocumentClick, true)
        this.$emit('closed')
      },
      toggle () {
        !this.isVisible ? this.open() : this.close()
      },
      handleDocumentClick (e) {
        if (!this.$el.getElementsByClassName('modal__container')[0].contains(e.target)) {
          if (!this.enableBackgroundClickToClose) {
            return
          } else if (!this.closeButton) {
            this.close()
          }
        }
      },
      breakdown () {
        this.heightOfContent = null
      }
    },
    updated () {
      const container = this.$refs.container
      if (this.setup && container) {
        if (this.heightOverride) {
          this.heightOfContent = this.heightOverride
        } else if (!this.autoWidth && !this.heightRange && this.autoHeight) {
          const contentHeight = container.clientHeight
          if (contentHeight !== this.heightOfContent) {
            this.heightOfContent = contentHeight - HEIGHT_OF_ACTIONS - PADDING_OF_MODAL
          }
        }
        this.setup = false
      }
    }
  }

</script>

<style lang="scss">
  $curves: 40px;
  $shadow: 4px;
  // .v-select .dropdown-menu {
  //   overflow-x: hidden;
  //   overflow: hidden;
  // }

  .modal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    @if ($z-4) {
      z-index: $z-4;
    } @else {
      z-index: 10999;
    }
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: unset;

    &__title {
      font-weight: bold;
      text-align: center;
      text-transform: uppercase;
    }

    &__is-relative {
      position: absolute;
    }

    &__bg {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: rgba(0, 0, 0, 0.6);
      z-index: 10;
      // pointer-events: none !important;
    }

    .extra-wide {
      max-width: 1500px;
    }

    &__container {
      z-index: 50;
      background: white;
      color: #444444;
      max-width: 1000px;
      max-height: 90%;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
    }

    &__body {
      display: flex;
      flex-direction: column;
    }

    &__content {
      flex: 1 1 auto;
      overflow-y: auto;
      padding: 20px;
      overflow-x: hidden;

      &--no-padding {
        padding: 0;
      }
    }

    &__header-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    &__actions {
      flex: 0 0 43px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 10px 15px 20px;

      button {
        margin-left: 10px;
      }
    }

    &__close-btn {
      align-self: flex-end;
      display: flex;
      width: 34px;
      height: 34px;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      svg {
        fill: black;
      }
    }

    &__loading-container {
      align-self: center;
      padding: 20px;
    }
  }

</style>



// WEBPACK FOOTER //
// Modal.vue?47489fb7